import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from '../common/Toast/toast';
import PopupDone, { usePopupDone } from "../common/PopupDone/PopupDone.jsx";
import { Server } from "lucide-react";
import {
  deleteMailerMeta,
  getMailerMetas,
} from "../../../services/MailerService";
import { useAuth } from "../../../context/AuthContext";
import NewCustomDataTable from "../common/NewCustomDataTable.jsx";
import SettingsFormModal from "../common/SettingsFormModal.jsx";
import AddMailerMeta from "./AddMailerMetas.jsx";
import useScrollLoadMore from "../common/useScrollLoadMore";
// Built to the same plan as /list-attendance, so it reads that page's
// single stylesheet rather than keeping one of its own.
import "../Trainer/TrainerDashboard.css";
import FilterSheet from "../common/FilterSheet/FilterSheet";
import useDebouncedCallback from "../common/useDebouncedCallback";
import { confirm } from '../common/Confirm/confirm';

const DASH = "—";

const PROVIDERS = [
  { value: "google", label: "Google" },
  { value: "outlook", label: "Outlook" },
  { value: "smtp", label: "SMTP" },
  { value: "sendgrid", label: "SendGrid" },
  { value: "mailgun", label: "Mailgun" },
];

const textOf = (value) => {
  const s = String(value ?? "").trim();
  return s === "" ? DASH : s;
};

const dateOf = (value) => {
  if (!value) return DASH;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? DASH
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
};

const providerLabel = (value) => {
  const found = PROVIDERS.find((p) => p.value === String(value || "").toLowerCase());
  return found ? found.label : textOf(value);
};

/* ── The record popup ────────────────────────────────────────────────
   Same plan as the attendance list's popup: title and a pgm-ident row of
   labelled facts in the header, pgm-section blocks in the body each led
   by a pgm-section-title, pgm-table for anything with more than one
   value, pgm-empty when there is nothing, and a footer whose actions sit
   left of a filled Close.
   ------------------------------------------------------------------ */
const MailerDetailModal = ({ detail, onClose, onEdit, onDelete, canEdit, canDelete }) => {
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (e) => {
      // A form opened from here sits on top; Escape is its, not this popup's.
      if (e.key === "Escape" && !document.querySelector(".pg-modal--over")) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detail, onClose]);

  // A delete from here swaps the record for the done panel, which closes
  // the popup five seconds later.
  const { done } = usePopupDone(Boolean(detail));

  if (!detail) return null;

  const isActive = !detail.isDeleted;
  const statusLabel = isActive ? "Active" : "Inactive";

  const connection = [
    { label: "Provider", value: providerLabel(detail.provider) },
    { label: "Host", value: textOf(detail.host) },
    { label: "Port", value: textOf(detail.port) },
    { label: "Auth user", value: textOf(detail.authUser) },
  ];

  const sender = [
    { label: "From name", value: textOf(detail.fromName) },
    { label: "From email", value: textOf(detail.fromEmail) },
  ];

  /* Only shown where the record actually carries OAuth settings -- an
     SMTP configuration has none, and a table of dashes says nothing.
     The refresh token itself is never printed; whether one exists is
     the only thing worth reporting about it. */
  const oauth = [
    ...(detail.clientId ? [{ label: "Client ID", value: detail.clientId, mono: true }] : []),
    ...(detail.redirectUrl ? [{ label: "Redirect URL", value: detail.redirectUrl }] : []),
  ];
  const hasOauth = oauth.length > 0 || Boolean(detail.refreshToken);

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mm-detail-title"
      onClick={onClose}
    >
      <div
        className="pg-modal-panel pg-modal-panel--wide"
        onClick={(e) => e.stopPropagation()}
      >
        {/* The configuration is what the record is about, so its name is
            the title -- in full, never clipped the way the list clips it. */}
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="mm-detail-title">
              {detail.name || "Mailer configuration"}
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Provider</dt>
                <dd>{providerLabel(detail.provider)}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>From</dt>
                <dd>{textOf(detail.fromEmail)}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Status</dt>
                <dd>{statusLabel}</dd>
              </div>
            </dl>
          </div>

          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? (
            <PopupDone message={done} onClose={onClose} />
          ) : (
            <>
              <section className="pgm-section">
                <h3 className="pgm-section-title">Connection</h3>
                <table className="pgm-table">
                  <thead>
                    <tr>
                      <th className="pgm-num">#</th>
                      <th>Detail</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {connection.map((f, i) => (
                      <tr key={f.label}>
                        <td className="pgm-num">{i + 1}</td>
                        <td>{f.label}</td>
                        <td>{f.value}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="pgm-num">{connection.length + 1}</td>
                      <td>Secure</td>
                      <td>
                        <span
                          className={`pgm-pill ${
                            detail.secure === true ? "pgm-pill--good" : "pgm-pill--bad"
                          }`}
                        >
                          {detail.secure === true ? "Yes" : "No"}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">Sender</h3>
                <table className="pgm-table">
                  <thead>
                    <tr>
                      <th className="pgm-num">#</th>
                      <th>Detail</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sender.map((f, i) => (
                      <tr key={f.label}>
                        <td className="pgm-num">{i + 1}</td>
                        <td>{f.label}</td>
                        <td>{f.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">OAuth</h3>
                {!hasOauth ? (
                  <p className="pgm-empty">
                    This configuration does not use OAuth.
                  </p>
                ) : (
                  <table className="pgm-table">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Detail</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {oauth.map((f, i) => (
                        <tr key={f.label}>
                          <td className="pgm-num">{i + 1}</td>
                          <td>{f.label}</td>
                          <td className={f.mono ? "pgm-mono" : undefined}>{f.value}</td>
                        </tr>
                      ))}
                      <tr>
                        <td className="pgm-num">{oauth.length + 1}</td>
                        <td>Refresh token</td>
                        <td>
                          {/* The token itself is never printed. Whether one is
                              stored is the only thing worth reporting. */}
                          <span
                            className={`pgm-pill ${
                              detail.refreshToken ? "pgm-pill--good" : "pgm-pill--bad"
                            }`}
                          >
                            {detail.refreshToken ? "Stored" : "Not stored"}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">Record</h3>
                <table className="pgm-table">
                  <thead>
                    <tr>
                      <th className="pgm-num">#</th>
                      <th>Detail</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="pgm-num">1</td>
                      <td>Status</td>
                      <td>
                        <span
                          className={`pgm-pill ${
                            isActive ? "pgm-pill--good" : "pgm-pill--bad"
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="pgm-num">2</td>
                      <td>Created</td>
                      <td>{dateOf(detail.createdAt)}</td>
                    </tr>
                    <tr>
                      <td className="pgm-num">3</td>
                      <td>Last updated</td>
                      <td>{dateOf(detail.updatedAt)}</td>
                    </tr>
                  </tbody>
                </table>
              </section>
            </>
          )}
        </div>

        {/* What you can do with the record sits with Close, at the end.
            Close stays last and is the only filled button, so the
            destructive one is never the default under the cursor. */}
        {/* The done panel replaces the actions once a delete lands. */}
        {!done && (
          <footer className="pg-modal-foot">
            <div className="pgm-actions">
              {canEdit && (
                <button type="button" className="pgm-btn" onClick={onEdit}>
                  Edit
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="pgm-btn pgm-btn--danger"
                  onClick={onDelete}
                >
                  Delete
                </button>
              )}
            </div>
            <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
              Close
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

const ListMailerMeta = () => {
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // 25 a batch. The list scrolls rather than paging, so this is how much
  // arrives per fetch, not how much the reader has to click through.
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  // Fetching the next scroll batch. Kept apart from `loading` because that
  // one draws a veil over the whole table -- right for a fresh load, wrong
  // for adding rows to the bottom of a list being read.
  const [loadingMore, setLoadingMore] = useState(false);
  const [detail, setDetail] = useState(null);
  /* The add/edit dialog. null is closed; {} is a new configuration;
     { recordId } edits that one. */
  const [formModal, setFormModal] = useState(null);
  /* Which step the form is on, and its question -- the dialog head asks
     it. Cleared with the dialog, so the next open starts on step one's
     copy rather than flashing the step the last one ended on. */
  const [formStep, setFormStep] = useState(null);

  // Closing the dialog and forgetting the step it was on are the same act,
  // so nothing can do one without the other.
  const closeForm = useCallback(() => {
    setFormModal(null);
    setFormStep(null);
  }, []);

  // What is typed into the filter section but not yet applied.
  const [searchDraft, setSearchDraft] = useState("");
  const [searchFieldDraft, setSearchFieldDraft] = useState("name");
  const [providerDraft, setProviderDraft] = useState("");

  // What is actually in force.
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("name");
  const [provider, setProvider] = useState("");

  const isFetching = useRef(false);
  const appendRef = useRef(false);
  const listRef = useRef(null);
  // The foot under the list. It is what the observer watches, and it
  // sits in the normal flow of the page -- no pane of its own.
  const listEndRef = useRef(null);
  const hasShownToast = useRef(false);

  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canView = hasPermission("list-mailer-meta", "view", "lms");
  const canAdd = hasPermission("list-mailer-meta", "add", "lms");
  const canEdit = hasPermission("list-mailer-meta", "update", "lms");
  const canDelete = hasPermission("list-mailer-meta", "delete", "lms");

  useEffect(() => {
    if (!canView) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to view Mailer Metadata.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [canView, navigate]);

  // ─── Fetch ────────────────────────────────────────────────────────────

  const fetchMailerMetas = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;
    // Captured before the request: the overlay must never appear for a
    // scroll batch, and reading the live ref after an await could get the
    // wrong answer if anything reset it meanwhile.
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);

    try {
      /* The endpoint takes one search pair, so a provider filter is sent
         as that pair. Picking a provider therefore replaces the typed
         search rather than narrowing it -- which is what the old page
         did too, only without saying so. */
      let value = searchTerm.trim();
      let field = value && searchField !== "all" ? searchField : "";
      if (provider) {
        value = provider;
        field = "provider";
      }
      if (!value) field = "";

      const response = await getMailerMetas(
        recordsPerPage,
        currentPage,
        value,
        field,
      );

      const records = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      const total = Number(
        response?.pagination?.total ?? (isAppend ? totalRecords : records.length),
      );
      setTotalRecords(total);

      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setRows((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...records.filter((r) => !seen.has(r.id))];
        });
      } else {
        setRows(records);
      }
      appendRef.current = false;
    } catch (error) {
      console.error("Error fetching mailer metadata:", error);
      toast.error("Error loading mailer metadata!");
      if (!isAppend) {
        setRows([]);
        setTotalRecords(0);
      }
      appendRef.current = false;
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetching.current = false;
    }
    // totalRecords is only read to hold the previous figure on an append;
    // depending on it would refetch every time it lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordsPerPage, currentPage, searchTerm, searchField, provider]);

  useEffect(() => {
    if (canView) fetchMailerMetas();
  }, [fetchMailerMetas, canView]);

  // ─── Scroll ───────────────────────────────────────────────────────────

  // The scroll asked for the next batch. Only ever moves forward, and only
  // while there is more on the server than is already on screen.
  const loadMore = useCallback(() => {
    if (isFetching.current || loading || loadingMore) return;
    if (rows.length >= totalRecords) return;
    const nextPage = Math.floor(rows.length / recordsPerPage) + 1;
    // Setting the page to what it already is changes no state and runs no
    // effect, which would strand appendRef at true and make the next fresh
    // load append to the list it was supposed to replace.
    if (nextPage === currentPage) return;
    appendRef.current = true;
    setCurrentPage(nextPage);
  }, [loading, loadingMore, rows.length, totalRecords, recordsPerPage, currentPage]);

  // The page is the scroller, so the foot of the list coming into view is
  // the signal for the next batch -- see useScrollLoadMore.
  useScrollLoadMore(listEndRef, loadMore, [loadMore, rows.length]);

  // ─── Handlers ─────────────────────────────────────────────────────────

  /* The search box does not wait for Apply. It runs itself once the
     typing stops -- the pickers beside it still wait, because
     choosing three of those should cost one refetch, not three. */
  const runSearch = useDebouncedCallback((term, field) => {
    appendRef.current = false;
    setSearchTerm(term.trim());
    setSearchField(field);
    setCurrentPage(1);
  });

  const handleApplyFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out
       its delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setSearchTerm(searchDraft.trim());
    setSearchField(searchFieldDraft);
    setProvider(providerDraft);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out
       its delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setSearchDraft("");
    setSearchFieldDraft("name");
    setProviderDraft("");
    setSearchTerm("");
    setSearchField("name");
    setProvider("");
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(Number(newLimit));
    setCurrentPage(1);
  };

  const hasActiveFilters =
    Boolean(searchTerm) || searchField !== "name" || Boolean(provider);

  const handleDelete = async (id) => {
    const result = await confirm({
      title: "Delete Confirmation",
      tone: 'danger',
      text: "Are you sure you want to delete this Mailer Configuration?",
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "No, Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteMailerMeta(id);
      // Gone: the popup that showed it swaps to the done panel and closes
      // itself five seconds later.
      // Rebuild the list from page 1 so the row is gone and the count is
      // right, rather than leaving a hole in a scrolled list.
      appendRef.current = false;
      if (currentPage === 1) fetchMailerMetas();
      else setCurrentPage(1);
      toast.success("The mailer configuration has been deleted successfully.");
    } catch (error) {
      console.error("Error deleting mailer configuration:", error);
      toast.error("Failed to delete the mailer configuration. Please try again.");
    }
  };

  // ─── Headers ──────────────────────────────────────────────────────────

  /* Five columns and no more. S.No, the port, whether the connection is
     secure, the OAuth settings, both timestamps and the row of action
     buttons are all in the popup a row opens -- the same trade the
     attendance list makes. */
  const headers = [
    {
      title: "Name",
      prop: "name",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.name ? (
          <span className="pg-strong">{row.name}</span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
    {
      title: "Provider",
      prop: "provider",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.provider ? (
          <span>{providerLabel(row.provider)}</span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
    {
      title: "Host",
      prop: "host",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.host ? <span>{row.host}</span> : <span className="pg-none">{DASH}</span>,
    },
    {
      title: "From Email",
      prop: "fromEmail",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.fromEmail ? (
          <span className="pg-course" title={row.fromEmail}>
            {row.fromEmail}
          </span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
    {
      title: "Status",
      prop: "isDeleted",
      filterable: true,
      sortable: true,
      cell: (row) => (
        <span className={`pg-pill ${row.isDeleted ? "pg-pill--bad" : "pg-pill--good"}`}>
          {row.isDeleted ? "Inactive" : "Active"}
        </span>
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="pg">

      {/* The form is two steps and reports which one it is on, so the
          dialog asks the step's question rather than repeating a fixed
          title over changing content. The fallbacks are what shows for
          the frame before the first report arrives. */}
      <SettingsFormModal
        open={Boolean(formModal)}
        eyebrow={formModal?.recordId ? "Edit mailer" : "New mailer"}
        title={formStep?.title || "Which server sends the mail?"}
        subtitle={
          formStep?.hint || "The provider, the host it connects to, and how."
        }
        labelledBy="mailer-form-title"
        size="wide"
        onClose={closeForm}
      >
        <AddMailerMeta
          embedded
          recordId={formModal?.recordId}
          onStepChange={(copy) => setFormStep(copy)}
          onCancel={closeForm}
          onDone={() => {
            // Saved: the dialog swaps the form for the done panel and closes
            // itself five seconds later (SettingsFormModal).
            // The record changed, so the view popup still open behind this form
            // would be showing the old one. It goes; the form shows the done panel.
            setDetail(null);
            // Rebuild from page 1 so a new configuration appears and an
            // edited one shows its new values.
            appendRef.current = false;
            if (currentPage === 1) fetchMailerMetas();
            else setCurrentPage(1);
          }}
        />
      </SettingsFormModal>

      <MailerDetailModal
        detail={detail}
        canEdit={canEdit}
        canDelete={canDelete}
        onClose={() => setDetail(null)}
        onEdit={() => {
          const id = detail?.id;
          if (id) setFormModal({ recordId: String(id) });
        }}
        onDelete={() => {
          const id = detail?.id;
          if (id) handleDelete(id);
        }}
      />

      {/* Every control that narrows the list lives here, the search box
          included -- the table's own toolbar is switched off below so the
          two are not stacked one above the other. Nothing here takes
          effect until Apply, so picking two filters is one refetch. */}
      <FilterSheet label="Filter mailer configurations">
        {/* Row one: the search box, the field it searches, and how many
            rows a load brings. */}
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="mm-search">
              Search
            </label>
            <input
              id="mm-search"
              type="text"
              className="pg-input pg-input--search"
              placeholder="Search by name, host or sender address..."
              value={searchDraft}
              onChange={(e) => {
                setSearchDraft(e.target.value);
                runSearch(e.target.value, searchFieldDraft);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApplyFilters();
              }}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="mm-search-field">
              Search in
            </label>
            <select
              id="mm-search-field"
              className="pg-select"
              value={searchFieldDraft}
              onChange={(e) => {
                setSearchFieldDraft(e.target.value);
                if (searchDraft.trim()) runSearch.flush(searchDraft, e.target.value);
              }}
            >
              <option value="all">All fields</option>
              <option value="name">Name</option>
              <option value="provider">Provider</option>
              <option value="host">Host</option>
              <option value="fromEmail">From email</option>
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="mm-rows">
              Rows per load
            </label>
            <select
              id="mm-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(e.target.value)}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Row two: the picker, then the buttons. */}
        <div className="pg-filters-row">
          <div className="pg-filters-fields pg-filters-fields--notrainer">
            <div className="pg-field">
              <label className="pg-label" htmlFor="mm-provider">
                Provider
              </label>
              <select
                id="mm-provider"
                className="pg-select"
                value={providerDraft}
                onChange={(e) => setProviderDraft(e.target.value)}
              >
                <option value="">All providers</option>
                {PROVIDERS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="pg-filters-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={handleApplyFilters}
            >
              Apply
            </button>
            <button
              type="button"
              className="pg-btn"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              Clear
            </button>

            {canAdd && (
              <>
                <span className="pg-filters-sep" aria-hidden="true" />
<button
                  type="button"
                  className="pg-btn pg-btn--main"
                  onClick={() => setFormModal({})}
                >
                  Add configuration
                </button>
              </>
            )}
          </div>
        </div>
      </FilterSheet>

      {/* --mailer: the rules that give a row its pointer and hover are
          unscoped, so the name has to be its own and unmistakable. */}
      <div className="td-list td-list--mailer" ref={listRef}>
        <NewCustomDataTable
          data={rows}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => setDetail(row)}
          // Every cell wraps its text in a span, so the default
          // ['TR', 'TD'] would leave the text itself unclickable and only
          // the padding around it would open the record.
          validRowClickTagNames="*"
          rowProps={(row) => ({
            tabIndex: 0,
            role: "button",
            "aria-label": `Open ${row.name || "this configuration"}`,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setDetail(row);
              }
            },
          })}
        />
      </div>

      {/* What the page buttons used to say, said once: how far down the
          list the reader is. Scrolling near the bottom fetches the next
          batch. */}
      <div className="pg-scroll-foot" ref={listEndRef}>
        {rows.length < totalRecords ? (
          <>
            <span className="pg-scroll-count">
              {rows.length} of {totalRecords}
            </span>
            {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
          </>
        ) : (
          totalRecords > 0 && (
            <span className="pg-scroll-count">
              All {totalRecords} configuration{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )
        )}
      </div>

      {!loading && rows.length === 0 && (
        <div className="pg-state">
          <span className="pg-state-icon">
            <Server size={26} aria-hidden="true" />
          </span>
          <h3 className="pg-state-title">No mailer configurations found</h3>
          <p className="pg-state-text">
            {hasActiveFilters
              ? "Nothing matches the filters in force."
              : "No mailer configuration has been created yet."}
          </p>
        </div>
      )}
    </div>
  );
};

export default ListMailerMeta;
